import type { UiKey } from "../i18n/ui";
import type { AudienceMode } from "../lib/audienceMode";

type Props = {
  t: (key: UiKey) => string;
  value: AudienceMode;
  onChange: (m: AudienceMode) => void;
  /** У вузькій колонці сайдбару: вирівнювання зліва, повна ширина сегментів. */
  variant?: "default" | "sidebar";
};

export function AudienceModeToggle({ t, value, onChange, variant = "default" }: Props) {
  const rootClass =
    "audience-toggle" + (variant === "sidebar" ? " audience-toggle--sidebar" : "");
  const hintFullKey =
    value === "simple" ? "audience.hintSimple" : "audience.hintTechnical";
  const hintDisplayKey =
    variant === "sidebar"
      ? value === "simple"
        ? "audience.hintSimpleSidebar"
        : "audience.hintTechnicalSidebar"
      : hintFullKey;

  return (
    <div className={rootClass} role="group" aria-label={t("audience.aria")}>
      <span className="lbl">{t("audience.label")}</span>
      <div className="seg audience-toggle__seg" role="presentation">
        <button
          type="button"
          className={"seg__btn" + (value === "simple" ? " seg__btn--on" : "")}
          aria-pressed={value === "simple"}
          onClick={() => onChange("simple")}
        >
          {t("audience.simple")}
        </button>
        <button
          type="button"
          className={"seg__btn" + (value === "technical" ? " seg__btn--on" : "")}
          aria-pressed={value === "technical"}
          onClick={() => onChange("technical")}
        >
          {t("audience.technical")}
        </button>
      </div>
      <p className="hint" title={variant === "sidebar" ? t(hintFullKey) : undefined}>
        {t(hintDisplayKey)}
      </p>
      <style>{`
        .audience-toggle {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.35rem;
          max-width: 22rem;
          text-align: right;
        }
        .lbl {
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--muted);
        }
        .audience-toggle__seg {
          flex-shrink: 0;
        }
        .hint {
          margin: 0;
          font-size: 0.72rem;
          line-height: 1.35;
          color: var(--muted);
          max-width: 20rem;
          /* EN hints differ in length vs uk/es — reserve lines so toggling View does not shift layout */
          min-height: 5.4rem;
          min-height: 6lh;
        }
        .audience-toggle--sidebar {
          align-items: stretch;
          max-width: none;
          text-align: left;
          margin-top: 0;
          padding-top: 0;
          border-top: none;
        }
        .audience-toggle--sidebar .audience-toggle__seg {
          width: 100%;
          padding: 0.1rem;
          gap: 0.1rem;
          border-radius: 6px;
          background: color-mix(in srgb, var(--surface2) 25%, transparent);
          border-color: color-mix(in srgb, var(--border) 45%, var(--bg));
        }
        .audience-toggle--sidebar .audience-toggle__seg .seg__btn {
          flex: 1;
          font-size: 0.65rem;
          font-weight: 600;
          padding: 0.2rem 0.35rem;
          min-height: 1.45rem;
          border-radius: 4px;
          line-height: 1.15;
        }
        .audience-toggle--sidebar .lbl {
          font-size: 0.62rem;
          opacity: 0.85;
          letter-spacing: 0.08em;
        }
        .audience-toggle--sidebar .hint {
          max-width: none;
          font-size: 0.65rem;
          line-height: 1.4;
          min-height: 0;
          text-wrap: balance;
          opacity: 0.88;
        }
        .audience-toggle__seg .seg__btn {
          white-space: nowrap;
        }
        @media (min-width: 900px) {
          .audience-toggle--sidebar {
            text-align: center;
            align-items: center;
          }
          .audience-toggle--sidebar .lbl {
            align-self: center;
          }
          .audience-toggle--sidebar .hint {
            text-align: center;
          }
        }
        @media (max-width: 899px) {
          .audience-toggle--sidebar .audience-toggle__seg .seg__btn {
            min-height: 2rem;
            padding: 0.28rem 0.4rem;
            font-size: 0.72rem;
          }
        }
        @media (max-width: 640px) {
          .audience-toggle:not(.audience-toggle--sidebar) {
            align-items: stretch;
            max-width: none;
            text-align: left;
          }
          .audience-toggle:not(.audience-toggle--sidebar) .audience-toggle__seg {
            width: 100%;
          }
          .audience-toggle:not(.audience-toggle--sidebar) .audience-toggle__seg .seg__btn {
            flex: 1;
          }
          .audience-toggle:not(.audience-toggle--sidebar) .hint {
            max-width: none;
          }
        }
      `}</style>
    </div>
  );
}
